import { Icon } from '@clientbase/clientbase-library'
import { Box, Stack, Typography } from '@mui/material'
import { useAuthContext } from 'context/AuthContext'
import { useRouter } from 'next/router'

import { settingsTabsParams } from 'utils/tabs'

import { theme } from 'styles/theme'

/**
 * Avisa, no formulário de cobrança/recorrência/plano/link, em qual plano de recebimento a
 * venda de cartão vai liquidar — e oferece o atalho para trocá-lo (SC-10370).
 *
 * O dado vem das `capabilities` do AuthContext (GET /v1/merchant_capabilities), que toda
 * sessão já carregou e que não exige permissão nenhuma. O GET /v3/credit_card_plans não
 * serviria: só responde a quem passa nas 5 travas de elegibilidade, ou seja, deixaria sem
 * resposta justamente quem não pode trocar de plano — a maior parte de quem cria cobrança.
 *
 * O atalho aparece para todos, inclusive para quem não é elegível: a seção de destino já
 * trata esse caso com um EmptyState ("A troca de plano não está disponível para a sua
 * conta. Fale com o suporte para saber mais."), que é resposta melhor do que não ter por
 * onde perguntar.
 *
 * Forma: uma linha de texto pequeno com o ícone `info`, sem caixa nem fundo. O aviso é
 * contexto do campo de parcelas, não alerta: como bloco informacional ele pesava mais que
 * o próprio formulário. O link é um <button> de verdade porque isto vive dentro do <form>
 * do modal de cobrança.
 */
const CreditCardPlanNotice = () => {
  const router = useRouter()
  const { capabilities } = useAuthContext()

  const planName = capabilities?.credit_card_plans?.current_plan_name
  const settlementText =
    capabilities?.credit_card_plans?.current_plan_settlement_text

  // "Não configurado" é o que o backend devolve quando a capability não tem bloco de
  // cartão. Avisar disso no meio de uma cobrança só geraria dúvida sem ação possível.
  if (!planName || planName === 'Não configurado') return null

  return (
    <Stack
      mx={2}
      mt={1}
      direction="row"
      gap={0.75}
      alignItems="flex-start"
      data-testid="credit-card-plan-notice"
    >
      <Box display="flex" pt="2px">
        <Icon
          icon="info"
          colorSX={theme.palette.neutral[400]}
          width="14px"
          height="14px"
        />
      </Box>

      <Typography variant="xsLight" color={theme.palette.neutral[300]}>
        <Typography
          component="span"
          variant="xsBold"
          color={theme.palette.neutral[300]}
        >
          Seu plano de recebimento: {planName}
        </Typography>
        {!!settlementText && (
          <>
            {'. '}
            <span>{settlementText}</span>
          </>
        )}{' '}
        <Typography
          component="button"
          type="button"
          variant="xsBold"
          onClick={() =>
            router.push(`/settings?tab=${settingsTabsParams.CREDIT_CARD_PLANS}`)
          }
          sx={{
            background: 'none',
            border: 0,
            color: theme.palette.info[200],
            display: 'inline',
            p: 0,
            ':hover': { cursor: 'pointer', textDecoration: 'underline' }
          }}
        >
          Trocar plano de recebimento
        </Typography>
      </Typography>
    </Stack>
  )
}

export default CreditCardPlanNotice
